import { Button, Flex, Paper, Text, Loader, Textarea } from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../hooks";
import { RootState } from "../store";
import {
  displayErrorMessage,
  displayServerErrorMessage,
  handleFetchingError,
} from "../shared/ErrorMessage";
import { initialize, updateField } from "../slices/form/sendGuestMessage";
import { useEffect } from "react";
import {
  SendGuestMessageRequest,
  useGetGuestNameQuery,
  useSendGuestMessageMutation,
} from "../slices/api/guestsApi";
import { formatFullName } from "../shared/utility";
import { IconSendMessage } from "../shared/components/Icons";

interface Props {
  guestId: string;
  isSenderHost: boolean;
  onClose: () => void;
}
const SendGuestMessage = (props: Props) => {
  const { data } = useAppSelector(
    (state: RootState) => state.sendGuestMessageForm
  );
  const {
    data: guestData,
    isError: isErrorFetchingGuest,
    error: guestError,
  } = useGetGuestNameQuery({
    id: props.guestId,
  });
  const [sendGuestMessage, { isLoading: isSendingMessage }] =
    useSendGuestMessageMutation();

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(initialize({ message: "" }));
  }, []);

  const sendMessage = async () => {
    if (data === undefined) {
      console.error("data is undefined but should not be");
      return;
    }
    if (data.message == null || data.message.trim() === "") {
      displayErrorMessage("Message is required.");
      return;
    }

    const request: SendGuestMessageRequest = {
      message: data.message,
      guestId: props.guestId,
    };

    try {
      await sendGuestMessage(request).unwrap();
      props.onClose();
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };

  if (isErrorFetchingGuest) {
    handleFetchingError(guestError);
    return null;
  }

  if (!data || !guestData) {
    return <Loader color="blue" />;
  }

  return (
    <>
      <Paper>
        <Text size="lg">
          Send Message to{" "}
          {formatFullName(guestData.firstName, guestData.lastName)}
        </Text>
        <Textarea
          mt="0"
          maxLength={1000}
          rows={3}
          label="Message (max 1000 characters)"
          value={data.message ?? ""}
          onChange={(event) =>
            dispatch(
              updateField({
                field: "message",
                value: event.currentTarget.value,
              })
            )
          }
        />
        {!props.isSenderHost && (
          <Text>
            To protect your privacy, messages are sent as emails without
            including your email address. For a better experience, share your
            contact info in your message so that you can communicate directly.
          </Text>
        )}

        <Flex justify="flex-end">
          <Button mt="md" mr="md" onClick={props.onClose}>
            Cancel
          </Button>
          <Button
            disabled={data.message === "" || isSendingMessage}
            mt="md"
            onClick={sendMessage}
            rightSection={
              isSendingMessage ? <Loader size={14} /> : <IconSendMessage />
            }
          >
            Send Message
          </Button>
        </Flex>
      </Paper>
    </>
  );
};

export default SendGuestMessage;
